import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import cn from 'clsx';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { locationYears } from '@/components/survey/business/first-location/LocationMoveYearInputDrawer';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { CTA, SelectInput, SelectDrawer, YearDrawer } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { useLegalAddress } from '@/lib/hooks/use-legal-address';
import { locationFormAtom, locationsDataAtom } from '@/lib/stores/survey/business';

type DrawerType = 'year' | 'city' | 'address1' | 'address2' | '';

/**
 * 주소 데이터를 관리하는 커스텀 훅
 */
const useAddressData = (formData: { city: string; address1?: string; address2?: string }) => {
  const { getSido, getSgg, getYmdg } = useLegalAddress();
  const [sido, setSido] = useState<string[]>([]);
  const [sgg, setSgg] = useState<string[] | null>([]);
  const [ymdg, setYmdg] = useState<string[] | null>([]);
  const isInit = useRef<boolean>(false);

  const fetchSido = useCallback(async (): Promise<void> => {
    try {
      const sidoList = await getSido();
      setSido(sidoList);
    } catch (error) {
      console.error('시/도 정보를 가져오는데 실패했습니다:', error);
    }
  }, [getSido]);

  const fetchYmdg = useCallback(
    async (city: string, address1: string = ''): Promise<void> => {
      if (!city) return;

      try {
        const ymdgList = await getYmdg(city, address1);
        setYmdg(ymdgList);
      } catch (error) {
        console.error('읍/면/동 정보를 가져오는데 실패했습니다:', error);
      }
    },
    [getYmdg]
  );

  const fetchSgg = useCallback(
    async (city: string): Promise<void> => {
      if (!city) return;

      try {
        const sggList = await getSgg(city);
        setSgg(sggList);
        if (sggList === null) {
          await fetchYmdg(city, '');
        }
      } catch (error) {
        console.error('시/군/구 정보를 가져오는데 실패했습니다:', error);
      }
    },
    [fetchYmdg, getSgg]
  );

  useEffect(() => {
    const initializeAddressValues = async (): Promise<void> => {
      if (isInit.current) return;

      isInit.current = true;
      if (sido.length === 0) {
        await fetchSido();
      }

      if (formData.city) {
        await fetchSgg(formData.city);

        if (formData.address1 || formData.address2) {
          await fetchYmdg(formData.city, formData.address1 || '');
        }
      }
    };

    initializeAddressValues();
  }, [formData.city, formData.address1, formData.address2, sido.length, fetchSgg, fetchSido, fetchYmdg]);

  return {
    sido,
    sgg,
    ymdg,
    fetchSgg,
    fetchYmdg
  };
};

export const LocationFormContainer = () => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();

  const [formData, setFormData] = useAtom(locationFormAtom);
  const [originData, setLocations] = useAtom(locationsDataAtom);

  const [openingDrawer, setOpeningDrawer] = useState<DrawerType>('');

  const { sido, sgg, ymdg, fetchSgg, fetchYmdg } = useAddressData(formData);

  const closeDrawer = (): void => {
    setOpeningDrawer('');
  };

  const yearItems = useMemo(
    () =>
      locationYears.map((year) => ({
        year,
        disabled: year < parseInt(originData.openingDate.slice(0, 4))
      })),
    [originData]
  );

  const resetFormData = () => {
    setFormData({
      index: -1,
      isFirstLocation: 'no',
      year: 0,
      city: ''
    });
  };

  const handleDeleteLocation = (): void => {
    sendClickEvent('survey_relocation-address-input_delete');

    showDialog({
      title: '삭제할까요?',
      description: '입력한 정보를 삭제할게요.',
      buttons: {
        secondary: {
          variant: 'tonal-secondary',
          text: '취소',
          onClick: () => sendClickEvent('survey_relocation-address-input_delete-cancel')
        },
        primary: {
          variant: 'filled-error',
          text: '삭제하기',
          onClick: () => {
            sendClickEvent('survey_relocation-address-input_delete-ok');

            const newLocations = originData.locations.slice();
            if (typeof formData?.index === 'number') {
              newLocations.splice(formData?.index, 1);
            }
            setLocations((prev) => ({
              ...prev,
              locations: newLocations
            }));
            resetFormData();
            router.back();
          }
        }
      }
    });
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    sendClickEvent('survey_relocation-address-input_confirm', eventAttributes);

    const inputLocation = {
      year: formData.year,
      address: `${formData.city} ${formData.address1} ${formData.address2}`,
      isFirstLocation: formData.isFirstLocation,
      isUnknownRelocationYear: false,
      isHometaxRegAddress: false
    };
    const newLocations = originData.locations.slice();
    if (formData.index > -1) {
      newLocations.splice(formData?.index, 1, inputLocation);
    } else {
      newLocations.push(inputLocation);
    }
    setLocations((prev) => ({
      ...prev,
      locations: newLocations
    }));
    router.back();
    resetFormData();
  };

  const handleCitySelect = async (city: string): Promise<void> => {
    try {
      // 시/군/구 정보 가져오기 (fetchSgg 내부에서 setSgg 호출)
      await fetchSgg(city);
      // 폼 데이터 업데이트 및 드로어 닫기
      setFormData((prev) => ({ ...prev, city, address1: '', address2: '' }));
      closeDrawer();
    } catch (error) {
      console.error('주소 정보를 가져오는데 실패했습니다:', error);
    }
  };

  const handleAddress1Select = async (address1: string): Promise<void> => {
    await fetchYmdg(formData.city, address1);
    setFormData((prev) => ({ ...prev, address1, address2: '' }));
    closeDrawer();
  };

  const handleAddress2Select = (address2: string): void => {
    setFormData((prev) => ({ ...prev, address2 }));
    closeDrawer();
  };

  const handleYearSelect = (year: number): void => {
    setFormData((prev) => ({ ...prev, year }));
    closeDrawer();
  };

  const canSelectAddress1 = (): boolean => {
    return Boolean(formData.city);
  };

  const canSelectAddress2 = (): boolean => {
    return Boolean(formData.city) && !(formData.city && sgg !== null && !formData.address1);
  };

  /**
   * 필드의 필수 여부를 확인하는 유틸리티 함수
   */
  const isFieldRequired = (fieldId: 'year' | 'city' | 'address1' | 'address2'): boolean => {
    // 연도와 시/도는 항상 필수
    if (fieldId === 'year' || fieldId === 'city') {
      return true;
    }
    // 직접창업 질문 'yes' 인 경우
    return originData.isStartBusinessDirectly;
  };

  /**
   * 필드가 선택 가능한지 확인하는 유틸리티 함수
   * 이 함수는 폼 유효성 검사에 사용됨 (UI의 disabled 상태와는 다름)
   */
  const isFieldDisabled = (fieldId: 'year' | 'city' | 'address1' | 'address2'): boolean => {
    if (fieldId === 'year' || fieldId === 'city') return false;
    if (fieldId === 'address1') return Boolean(formData.city) && sgg === null;
    if (fieldId === 'address2') return Boolean(formData.address1) && ymdg === null;
    return false;
  };

  /**
   * 필드가 유효한 값을 가지고 있는지 확인하는 유틸리티 함수
   */
  const isFieldValid = (fieldId: 'year' | 'city' | 'address1' | 'address2'): boolean => {
    if (fieldId === 'year') {
      return Boolean(formData.year);
    }

    if (fieldId === 'city') {
      return Boolean(formData.city);
    }

    // 주소 필드(address1, address2)에 대한 유효성 검사를 처리하는 헬퍼 함수
    const isAddressFieldValid = (field: 'address1' | 'address2'): boolean => {
      // 필수 필드일때
      if (isFieldRequired(field)) {
        // 필수이지만 선택할 수 없는 경우 - (시/도에 해당하는 시군구가 없음 또는 시군구에 해당하는 읍면동이 없음)
        if (isFieldDisabled(field)) {
          return true;
        }
        return Boolean(formData[field]);
      }
      // optional
      return true;
    };

    if (fieldId === 'address1' || fieldId === 'address2') {
      return isAddressFieldValid(fieldId);
    }

    return false;
  };

  /**
   * 폼 제출 버튼 활성화 여부를 확인하는 함수
   * @returns 필수 필드가 모두 입력되었는지 여부
   */
  const isFormValid = (): boolean => {
    return isFieldValid('year') && isFieldValid('city') && isFieldValid('address1') && isFieldValid('address2');
  };

  return (
    <>
      <SurveyTopNavigation
        onBackClick={() => {
          sendClickEvent('survey_relocation-address-input_back');

          router.back();
        }}
        rightButton={
          formData?.index > -1
            ? {
                label: '삭제',
                onClick: handleDeleteLocation
              }
            : undefined
        }
      />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form onSubmit={handleSubmit}>
          <h1 className={common.content_title}>
            사업장을 {formData?.isFirstLocation === 'yes' ? '최초 등록한' : '이전한'}
            <br />
            연도와 주소지를 입력해주세요
          </h1>

          <div className={'flex flex-col gap-3 pb-9'}>
            <SelectInput
              id={'year'}
              required={isFieldRequired('year') ? 'required' : 'optional'}
              label={formData?.isFirstLocation === 'yes' ? '최초 등록 연도' : '이전 연도'}
              value={formData?.year ? `${formData.year}` : ''}
              onClick={() => {
                sendClickEvent('survey_relocation-address-input_select-year', eventAttributes);

                setOpeningDrawer('year');
              }}
            />
            <SelectInput
              id={'city'}
              required={isFieldRequired('city') ? 'required' : 'optional'}
              label={'시/도'}
              value={formData.city}
              onClick={() => {
                sendClickEvent('survey_relocation-address-input_select-city', eventAttributes);

                setOpeningDrawer('city');
              }}
            />
            <SelectInput
              id={'address1'}
              required={isFieldRequired('address1') ? 'required' : 'optional'}
              label={'시/구/군'}
              disabled={isFieldDisabled('address1')}
              value={formData.address1 ?? ''}
              onClick={() => {
                sendClickEvent('survey_relocation-address-input_select-address1', eventAttributes);

                if (!canSelectAddress1()) {
                  showDialog({ title: '시/도부터 먼저 선택해주세요' });
                  return;
                }
                setOpeningDrawer('address1');
              }}
            />
            <SelectInput
              id={'address2'}
              required={isFieldRequired('address2') ? 'required' : 'optional'}
              label={'읍/면/동'}
              disabled={isFieldDisabled('address2')}
              value={formData.address2 ?? ''}
              onClick={() => {
                sendClickEvent('survey_relocation-address-input_select-address2', eventAttributes);

                if (!canSelectAddress2()) {
                  showDialog({ title: '시/구/군부터 먼저 선택해주세요' });
                  return;
                }
                setOpeningDrawer('address2');
              }}
            />
          </div>

          <CTA type={'submit'} disabled={!isFormValid()}>
            입력 완료
          </CTA>
        </form>
      </main>

      {/* 연도 선택 */}
      <YearDrawer title={'선택해주세요'} isVisible={openingDrawer === 'year'} onClose={closeDrawer} items={yearItems} onSelectYear={handleYearSelect} />
      {/* 시도 */}
      <SelectDrawer isVisible={openingDrawer === 'city'} onClose={closeDrawer} title={'선택해주세요'} items={sido} onClickItem={handleCitySelect} />
      {/* 시군구 */}
      <SelectDrawer
        isVisible={openingDrawer === 'address1'}
        onClose={closeDrawer}
        title={'선택해주세요'}
        items={sgg || []}
        onClickItem={handleAddress1Select}
      />
      {/* 읍면동 */}
      <SelectDrawer
        isVisible={openingDrawer === 'address2'}
        onClose={closeDrawer}
        title={'선택해주세요'}
        items={ymdg || []}
        onClickItem={handleAddress2Select}
      />
    </>
  );
};
